import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Permissions, TokenPermissions } from '../data/permissions';
import { TemporaryToken } from '../data/temporaryToken';
import { getDecryptedCode } from '../utils/generateRandomToken';
import { BookService } from './book.service';
import { ConfigService } from './config.service';
import { PermissionService } from './permission.service';

const LOCAL_STORAGE_TOKEN = 'DBAuthTkn';

export interface LocatorAccessResponse {
  //tkn?: string;
  bookUrl?: string;
  visorUrl?: string;
  reportUrl?: string;
}

export enum ACCESS_ERROR_CODE {
  INVALID_CODE,
  INVALID_LOGIN,
  CONNECTION_ERROR,
  NO_PERMISSION,
  INVALID_BOOK_ACCESS
}

@Injectable({
  providedIn: 'root'
})
export class UserAccessService {

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private permissionService: PermissionService,
    private bookService: BookService
  ) {
  }

  grantAccess(code: string, bookToken: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {

      // Si es demo, hacemos las comprobaciones de demo (código maestro y desencriptar el código que se genera en el portátil de demo)
      if (environment.demo) {
        if (code == 'DeBRAIN2022') {
          resolve();
        } else {
          const millis = getDecryptedCode(code);
          const nowMillis = new Date().getTime();
          // 60min
          if ((nowMillis + 3500000) > millis && (nowMillis - 3500000) < millis) {
            resolve();
          } else {
            reject(ACCESS_ERROR_CODE.INVALID_CODE);
          }
        }
      } else {
        // Si no es demo, comprobamos el acceso contra el servidor
        this.checkAccess(bookToken, code).then(res => {
          // Si nos devuelve un token, lo guardamos en localStorage
          if (res != null) {
            localStorage.setItem(LOCAL_STORAGE_TOKEN, res.authTkn);
            this.permissionService.permissions = new Set(Permissions.sanitizeAll(res.permissions));
            if (this.permissionService.hasPermission(TokenPermissions.SEE_BOOK)) {
              resolve();
            }
            else {
              reject(ACCESS_ERROR_CODE.NO_PERMISSION);
            }
          } else {
            reject(ACCESS_ERROR_CODE.INVALID_CODE);
          }
        }).catch(e => {
          console.error(e);
          reject(ACCESS_ERROR_CODE.INVALID_BOOK_ACCESS);
        })
      }

    });

  }

  grantAccessExternalToken(externalToken: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      if (environment.demo) {
        window.location.href = '/acceso?tkn=demoMR_STPLUS'
      } else {
        // Si no es demo, comprobamos el acceso contra el servidor
        this.checkAccessWithExternalToken(externalToken).then(res => {

          if (res != null) {
            localStorage.setItem(LOCAL_STORAGE_TOKEN, res.authTkn);
            // this.permissionService.permissions = new Set(Permissions.sanitizeAll(res.permissions));
            // if (this.permissionService.hasPermission(TokenPermissions.SEE_BOOK)) {
            resolve(res.bookTkn);
            // }
          } else {
            reject(ACCESS_ERROR_CODE.INVALID_CODE);
          }
        }).catch(e => {
          console.error(e);
          reject(ACCESS_ERROR_CODE.CONNECTION_ERROR);
        })
      }

    });

  }

  grantAccessWithLocator(locator: string, code: string): Promise<LocatorAccessResponse> {
    return new Promise<LocatorAccessResponse>((resolve, reject) => {
      if (environment.demo) {
        window.location.href = '/acceso?tkn=demoMR_STPLUS'
      } else {
        // Si no es demo, comprobamos el acceso contra el servidor
        this.checkAccessWithLocator(locator, code).then(res => {
          if (res != null) {
            resolve(res);
          } else {
            reject(ACCESS_ERROR_CODE.INVALID_CODE);
          }
        }).catch(e => {
          console.error(e);
          reject(ACCESS_ERROR_CODE.INVALID_BOOK_ACCESS);
        })
      }

    });

  }

  grantAccessFromCookies(portalToken: string) {
    return new Promise<void>((resolve, reject) => {
      if (environment.demo) {
        window.location.href = '/acceso?tkn=demoMR_STPLUS'
      } else {
        // Si no es demo, comprobamos el acceso contra el servidor
        this.checkAccessWithPortalAuth(portalToken).then(res => {
          // Si nos devuelve un objeto con la info, lo guardamos en bookService
          if (res != null) {
            this.bookService.temporaryToken = res;
            localStorage.setItem(LOCAL_STORAGE_TOKEN, res.authTkn);
            this.permissionService.permissions = new Set([TokenPermissions.SEE_BOOK, TokenPermissions.DOWNLOAD, TokenPermissions.OPEN_IMAGE_PREVIEW, TokenPermissions.SEE_STUDY, TokenPermissions.SHARE]);
            if (res.urlHistoric != null && res.urlHistoric != '') {
              this.permissionService.permissions.add(TokenPermissions.SEE_HISTORIC);
            }
            resolve();
          } else {
            reject(ACCESS_ERROR_CODE.INVALID_LOGIN);
          }
        }).catch(e => {
          console.error(e);
          reject(ACCESS_ERROR_CODE.CONNECTION_ERROR);
        })
      }

    });
  }

  grantAccessWithUser(bookToken: string, userName: string, password: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // Comprobamos el acceso contra el servidor
      this.checkAccessWithUser(bookToken, userName, password).then(res => {
        // Si nos devuelve un token, lo guardamos en localStorage
        if (res != null) {
          localStorage.setItem(LOCAL_STORAGE_TOKEN, res.authTkn);
          this.permissionService.permissions = new Set(Permissions.sanitizeAll(res.permissions));
          if (this.permissionService.hasPermission(TokenPermissions.SEE_BOOK)) {
            resolve();
          }
          else {
            reject(ACCESS_ERROR_CODE.NO_PERMISSION);
          }
        } else {
          reject(ACCESS_ERROR_CODE.INVALID_LOGIN);
        }
      }).catch(e => {
        console.error(e);
        reject(ACCESS_ERROR_CODE.CONNECTION_ERROR);
      })
    });

  }

  grantAccessWithLocatorOnly(token: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // Si no es demo, comprobamos el acceso contra el servidor
      this.checkAccessWithLocatorOnly(token).then(res => {
        // Si nos devuelve un token, lo guardamos en localStorage
        if (res != null) {
          localStorage.setItem(LOCAL_STORAGE_TOKEN, res.authTkn);
          this.permissionService.permissions = new Set(Permissions.sanitizeAll(res.permissions));
          if (this.permissionService.hasPermission(TokenPermissions.SEE_BOOK)) {
            resolve();
          }
          else {
            reject(ACCESS_ERROR_CODE.NO_PERMISSION);
          }
        } else {
          reject(ACCESS_ERROR_CODE.INVALID_CODE);
        }
      }).catch(e => {
        console.error(e);
        reject(ACCESS_ERROR_CODE.CONNECTION_ERROR);
      })
      

    });

  }

  checkAccess(token: string, code: string): Promise<{ authTkn: string, permissions: TokenPermissions[] } | null> {
    return new Promise((resolve, reject) => {
      this.configService.BOOK_URL.then(confURL => {
        const url = `${confURL}/bookAccess/checkAccess?token=${token}`;
        this.http.post<{ authTkn: string, permissions: TokenPermissions[] }>(url, code, { withCredentials: true, responseType: 'json' }).subscribe(res => {
          if (res == null) {
            resolve(null);
          } else {
            resolve(res);
          }
        }, error => reject(error))
      })
    });
  }

  checkAccessWithUser(token: string, userName: string, password: string): Promise<{ authTkn: string, permissions: TokenPermissions[] } | null> {
    return new Promise((resolve, reject) => {
      this.configService.BOOK_URL.then(confURL => {
        const url = `${confURL}/bookAccess/checkAccessWithUser?token=${token}`;
        this.http.post<{ authTkn: string, permissions: TokenPermissions[] }>(url, { userName, password }, { withCredentials: true, responseType: 'json' }).subscribe(res => {
          if (res == null) {
            resolve(null);
          } else {
            resolve(res);
          }
        }, error => reject(error))
      })
    });
  }

  checkAccessWithPortalAuth(portalToken: string): Promise<TemporaryToken> {
    return new Promise<TemporaryToken>((resolve, reject) => {
      this.configService.BOOK_URL.then(confURL => {
        const url = `${confURL}/bookAccess/checkAccessWithPortalAuth`;
        this.http.post<TemporaryToken>(url, portalToken, { withCredentials: true, responseType: 'json' }).subscribe(res => {
          if (res == null) {
            reject(null);
          } else {
            resolve(res);
          }
        }, error => reject(error))
      })
    });
  }

  checkAccessWithLocator(locator: string, code: string): Promise<LocatorAccessResponse | null> {
    const access = {
        locator: locator?.toUpperCase()?.trim(),
        code: (code == null) ? '' : code.toUpperCase().trim()
    }
    return new Promise((resolve, reject) => {
      this.configService.BOOK_URL.then(confURL => {
        const url = `${confURL}/bookAccess/checkAccessWithLocator`;
        this.http.post<LocatorAccessResponse>(url, access, { withCredentials: true, responseType: 'json' }).subscribe(res => {
          if (res == null) {
            resolve(null);
          } else {
            resolve(res);
          }
        }, error => reject(error))
      })
    });
  }

  checkAccessWithExternalToken(externalToken: string): Promise<{ authTkn: string, bookTkn: string } | null> {
    return new Promise((resolve, reject) => {
      this.configService.BOOK_URL.then(confURL => {
        const url = `${confURL}/bookAccess/checkAccessWithExternalToken`;
        this.http.post<{ authTkn: string, bookTkn: string }>(url, externalToken, { withCredentials: true, responseType: 'json' }).subscribe(res => {
          resolve(res);
        }, error => reject(error))
      })
    });
  }

  checkAccessWithLocatorOnly(token: string): Promise<{ authTkn: string, permissions: TokenPermissions[] } | null> {
    return new Promise((resolve, reject) => {
      this.configService.BOOK_URL.then(confURL => {
        const url = `${confURL}/bookAccess/checkAccessWithLocatorOnly`;
        this.http.post<{ authTkn: string, permissions: TokenPermissions[] }>(url, token, { withCredentials: true, responseType: 'json' }).subscribe(res => {
          resolve(res);
        }, error => reject(error))
      })
    });
  }

  sendMailForLastBook(email: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.configService.BOOK_URL.then(confURL => {
        const url = `${confURL}/bookAccess/sendMailForLastBook`;
        this.http.post(url, email, { withCredentials: true, responseType: 'text' }).subscribe({
          next: (res) => resolve(res),
          error: (error) => {
            console.error(error);
            reject();
          }
        })
      })
    });
  }

}
